import React, { useEffect, useState, useRef, useCallback } from "react";
import { Box, Button, Container, Typography, useTheme, useMediaQuery, Checkbox, FormControlLabel, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, RadioGroup, Radio } from "@mui/material";
import { FilePond, registerPlugin, FileStatus } from "react-filepond";
import "filepond/dist/filepond.min.css";
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import ImageUploaded from "./imageUploaded";
import ImageGood from "./ImageGood";
import ImageBad from "./ImageBad";
import ProceedNotGradable from "./ProceedNotGradable";
import ImageProcessingScreen from "./ImageProcessingScreen";
import DuplicateImageError from "./DuplicateImageError";
import NonFundusError from "./NonFundusError";
import WrongEyeError from "./WrongEyeError";
import ImageUploadedStatus from "./ImageUploadedStatus";
import axios from "axios";
import config from "../../config";
import FullPageLoader from "../helper/FullPageLoader";
import { useLocation } from "react-router-dom";
import { checkImageDuplicates, getCachedImageHash } from "../../utils/imageHashing";
import { useModel } from "../../context/ModelContext";
import { checkModelExists, checkFundusModelExists, checkEyesModelExists } from "../../services/modelService";
import { saveScreeningImageDraft } from "../../db";
import { isMobileBrowser } from "../../utils/deviceDetection";
import { gcYield } from "../../utils/asyncUtils";
import { isDicomExtension, isDicomCandidate, convertDicomToImageFile, formatDicomConversionError } from "../../utils/dicomToImage";
import { preprocessImage } from "../../utils/imageProcessing";
import { terminateOpenCVWorker } from "../../services/opencvWorkerManager";

/** OpenCV preprocess in opencv worker, then transfer tensor to ONNX worker. */
async function preprocessAndCacheTensor(onnxClient, tensorId, file) {
    const { normalizedData } = await preprocessImage(file, { skipPreviewBlobs: true });
    await onnxClient.cacheTensor(tensorId, normalizedData);
}

function isAcceptedEyeUploadFile(file) {
    if (!file) return false;
    if (file.type && file.type.startsWith("image/")) return true;
    return isDicomExtension(file);
}

/** FilePond row after validation (raster replaces .dcm in state). */
function makeValidatedPondFileItem(file) {
    return [
        {
            file,
            id: `validated-${file.name}-${file.size}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
            status: FileStatus.IDLE,
            serverId: null,
        },
    ];
}

/** FilePond item, File, Blob, or restored draft { data, type, name } */
function normalizeImageForDraft(src) {
    if (!src) return null;
    if (src instanceof File || src instanceof Blob) return src;
    if (typeof src === "object" && src.data && src.type) return src;
    if (Array.isArray(src) && src.length > 0 && src[0]?.file) return src[0].file;
    return null;
}

async function persistScreeningImageDraftNow(leftSrc, rightSrc) {
    try {
        const toArrayBuffer = (obj) => {
            if (!obj) return Promise.resolve(null);
            if (obj instanceof File || obj instanceof Blob) {
                return new Promise((resolve) => {
                    const r = new FileReader();
                    r.onload = () => resolve({ data: r.result, type: obj.type, name: obj.name || "image" });
                    r.onerror = () => resolve(null);
                    r.readAsArrayBuffer(obj);
                });
            }
            if (obj && typeof obj === "object" && obj.data && obj.type) {
                return Promise.resolve({ data: obj.data, type: obj.type, name: obj.name || "image" });
            }
            return Promise.resolve(null);
        };
        const l = normalizeImageForDraft(leftSrc);
        const r = normalizeImageForDraft(rightSrc);
        if (!l && !r) return;
        const [left_image, right_image] = await Promise.all([toArrayBuffer(l), toArrayBuffer(r)]);
        if (!left_image && !right_image) return;
        await saveScreeningImageDraft({
            patient_id: localStorage.getItem("patient_id"),
            report_id: localStorage.getItem("report_id"),
            left_image,
            right_image,
        });
    } catch (e) {
        /* best-effort */
    }
}

const options = [
    {label: "Patient is monocular", value: "Patient is monocular"},
    {label: "Unclear image due to the presence of other ophthalmic conditions", value: "Unclear image due to the presence of other ophthalmic conditions"},
];

const handleImageValueSet = (file, eye, formik) => {
    if(eye === "left") {
        const value = Array.isArray(file) && file.length>0 ? file[0].file : file;
        formik.setFieldValue("image_file.left.image", value);
    }
    else if(eye === "right") {
        const value = Array.isArray(file) && file.length>0 ? file[0].file : file;
        formik.setFieldValue("image_file.right.image", value);
    }
};

registerPlugin(FilePondPluginFileValidateType);

const ImageUpload = ({handleBack, handleSubmit, formik, setActiveStep}) => {
    const { fundusSession, loadFundusModel, releaseFundusSession, eyesSession, loadEyesModel, releaseEyesSession, session, loadModel, releaseSession, onnxClient, resetOnnxClient } = useModel();

    // Mobile (Safari iOS + Chrome Android): load Fundus/Eyes/DRDME only when needed; release ONNX sessions aggressively.
    // DRDME is dropped after each pipeline where possible — keeps WASM heap from stacking three models + decode RAM.
    const mobileSequentialOnnx = isMobileBrowser();

    // On mobile, terminate BOTH workers when leaving this screen (submit, back, or step change).
    // WASM heap never shrinks after session.release() — only worker termination returns it to the OS.
    // A fresh worker on the next patient starts with a zero heap, preventing cumulative OOM growth.
    //  - resetOnnxClient (from ModelContext, not resetOnnxWorkerClient directly): also clears session
    //    state so the next patient's loadFundusModel() doesn't short-circuit.
    //  - terminateOpenCVWorker: frees the ~180MB OpenCV preprocessing heap that otherwise lives the
    //    whole session (previously only freed on logout). Re-spawns lazily via waitForOpenCvReady()
    //    on the next patient's first image, and proactively via initOpenCVWorker() on screen mount.
    useEffect(() => {
        if (!mobileSequentialOnnx) return;
        return () => { resetOnnxClient(); terminateOpenCVWorker(); };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    /** Phones: drop DRDME WASM after inference — prevents heap staying high across many uploads */
    const releaseMobileDrdmeIfNeeded = useCallback(async () => {
        if (!mobileSequentialOnnx) return;
        releaseSession();
        await gcYield(600);
    }, [mobileSequentialOnnx, releaseSession]);

    const header = {
        "Authorization": "Token "+localStorage.getItem("optha-token")
    };
    // Helper function to check if image data is valid
    const isValidImageData = (imageData) => {
        if (!imageData) return false;
        if (imageData instanceof File || imageData instanceof Blob) return true;
        if (typeof imageData === 'string' && imageData.length > 0) return true;
        // Handle arrays (FilePond format: [{file: File, id: ..., status: ...}])
        if (Array.isArray(imageData) && imageData.length > 0) {
            return imageData[0].file && (imageData[0].file instanceof File || imageData[0].file instanceof Blob);
        }
        if (typeof imageData === 'object' && Object.keys(imageData).length > 0) {
            // Check if it has a file property with valid data
            if (imageData.file && (imageData.file instanceof File || imageData.file instanceof Blob)) return true;
            // Restored draft / IndexedDB format: { data: ArrayBuffer, type, name }
            if (imageData.data && imageData.type) return true;
        }
        return false;
    };

    const [leftEye, setLeftEye] = useState(
        isValidImageData(formik.values.image_file.left.image) ? formik.values.image_file.left.image : null
    );
    const [rightEye, setRightEye] = useState(
        isValidImageData(formik.values.image_file.right.image) ? formik.values.image_file.right.image : null
    );
    const [leftGradable, setLeftGradable] = useState();
    const [proceedNotLeftGradable, setProceedNotLeftGradable] = useState(false);
    const [rightGradable, setRightGradable] = useState();
    const [proceedNotRightGradable, setProceedNotRightGradable] = useState(false);
    // Track if image was uploaded via "proceed" option (for yellow status message)
    const [leftEyeProceeded, setLeftEyeProceeded] = useState(false);
    const [rightEyeProceeded, setRightEyeProceeded] = useState(false);
    const [showLeftImageConfirmation, setShowLeftImageConfirmation] = useState(false);
    const [showRightImageConfirmation, setShowRightImageConfirmation] = useState(false);
    const [disabledProceed, setDisabledProceed] = useState(true);
    const [loading, setLoading] = useState(false);
    const [singleImageMode, setSingleImageMode] = useState(false);
    const [selectedSingleImageReason, setSelectedSingleImageReason] = useState('');
    const [showSingleImageWarning, setShowSingleImageWarning] = useState(false);
    const [duplicateInfo, setDuplicateInfo] = useState(null);
    /** Heavy File/Blob — stored in ref so React never batches-renders from it. */
    const pendingImageRef = useRef(null);
    /** Cached Float32Array tensor from the failed attempt, reused if user proceeds to skip re-preprocessing. */
    const pendingTensorRef = useRef(null);
    const [pendingEye, setPendingEye] = useState(null);
    
    // New state for processing flow
    const [isProcessing, setIsProcessing] = useState(false);
    const [processingMessage, setProcessingMessage] = useState("Uploading image...");
    const [processingSubMessage, setProcessingSubMessage] = useState("This may take a few minutes");
    const [showDuplicateError, setShowDuplicateError] = useState(false);
    const [showNonFundusError, setShowNonFundusError] = useState(false);
    const [showWrongEyeError, setShowWrongEyeError] = useState(false);
    // Per-eye session count: left 3 uploads and right 3 uploads counted separately (show proceed on 3rd per eye)
    const [sessionNonFundusCount, setSessionNonFundusCount] = useState({ left: 0, right: 0 });
    /** Source of truth for reject count (avoids stale React state inside long async handleImageUpload). */
    const sessionNonFundusCountRef = useRef({ left: 0, right: 0 });
    const [currentNonFundusAttempt, setCurrentNonFundusAttempt] = useState(1); // Current attempt number for display

    /** After a successful fundus path or 3rd-step proceed, reset so the next non-fundus upload starts at step 1. */
    const resetNonFundusAttemptStateForEye = (eyeKey) => {
        sessionNonFundusCountRef.current = { ...sessionNonFundusCountRef.current, [eyeKey]: 0 };
        setSessionNonFundusCount((prev) => ({ ...prev, [eyeKey]: 0 }));
        setCurrentNonFundusAttempt(1);
    };

    const bumpSessionNonFundusCountForEye = (eye) => {
        const prev = sessionNonFundusCountRef.current;
        const newNonFundusCount = (prev[eye] || 0) + 1;
        sessionNonFundusCountRef.current = { ...prev, [eye]: newNonFundusCount };
        setSessionNonFundusCount({ ...sessionNonFundusCountRef.current });
        setCurrentNonFundusAttempt(newNonFundusCount);
        return newNonFundusCount;
    };
    const [isSimilarityScoreIssue, setIsSimilarityScoreIssue] = useState(false); // Track if error is from similarity score >= 80
    const [wrongEyeInfo, setWrongEyeInfo] = useState(null);
    const currentProcessingFileRef = useRef(null);
    const [currentProcessingEye, setCurrentProcessingEye] = useState(null);
    const [showModelsUnavailableError, setShowModelsUnavailableError] = useState(false);
    const [modelsUnavailableMessage, setModelsUnavailableMessage] = useState('');
    /** Dialog title — only "Models not available" when failure is actually model/download related */
    const [modelsErrorDialogTitle, setModelsErrorDialogTitle] = useState('Models not available');

    const MODELS_UNAVAILABLE_MSG = 'AI models could not be loaded. Please connect to the internet, then log out and log back in to download the required models. You cannot proceed with screening until the models are available.';
    const GENERIC_PROCESSING_ERROR_TITLE = 'Unable to process image';
    // User-friendly message when models are missing or download failed (avoids showing raw errors like "Failed to fetch")
    const getModelsUnavailableUserMessage = (rawMessage) => {
        if (!rawMessage) return null;
        const lower = String(rawMessage).toLowerCase();
        if (lower.includes('fetch') || lower.includes('network') || lower.includes('download failed') || lower.includes('not available')) {
            return MODELS_UNAVAILABLE_MSG;
        }
        if (lower.includes('model') && (lower.includes('download') || lower.includes('load') || lower.includes('cancel') || lower.includes('not found') || lower.includes('failed'))) {
            return MODELS_UNAVAILABLE_MSG;
        }
        return null;
    };
    const isModelUnavailableError = (err) => !!getModelsUnavailableUserMessage(err?.message);
    // Flag to prevent formik sync immediately after setting proceeded image
    const skipFormikSyncRef = useRef({ left: false, right: false });
    
    const leftEyeRef = useRef(null);
    const rightEyeRef = useRef(null);
    const failedDueToModelsRef = useRef(false);
    /** True only when failure was STEP 0 (sessions not ready) — do not strip the user's selected file */
    const modelPrecheckFailedRef = useRef(false);
    /** FilePond fires onupdatefiles multiple times per pick (e.g. INIT → IDLE); validate once per item id. */
    const lastValidatedPondItemIdRef = useRef({ left: null, right: null });
    /** Prevent overlapping handleImageUpload for the same eye (FilePond + hidden input). */
    const validationInFlightRef = useRef({ left: false, right: false });
    const leftFilePondRef = useRef(null);
    const rightFilePondRef = useRef(null);
    /** Bump to remount FilePond — unmount runs full `_pond.destroy()`. Do not assign ref.current = null here: a setTimeout can run after React attached the new instance and would clear a live ref (breaks re-uploads on iOS). */
    const [leftFilePondMountKey, setLeftFilePondMountKey] = useState(0);
    const [rightFilePondMountKey, setRightFilePondMountKey] = useState(0);
    const teardownFilePondLeft = useCallback(() => {
        try {
            if (leftFilePondRef.current) {
                leftFilePondRef.current.removeFiles();
            }
        } catch (_) {}
        setLeftFilePondMountKey((k) => k + 1);
    }, []);
    const teardownFilePondRight = useCallback(() => {
        try {
            if (rightFilePondRef.current) {
                rightFilePondRef.current.removeFiles();
            }
        } catch (_) {}
        setRightFilePondMountKey((k) => k + 1);
    }, []);
    /**
     * Drop all heavy references for a rejected upload that the user will not proceed with.
     * Called on non-fundus / Mahalanobis rejection attempts 1 and 2 (attempt 3 keeps the file
     * in pendingImageRef so the user can "Proceed with referral").
     * Does NOT clear pendingEye — dialogs still need it to know which eye the error is for.
     */
    const clearRejectedEyeState = useCallback(async (eye) => {
        try {
            pendingImageRef.current = null;
            pendingTensorRef.current = null;
            currentProcessingFileRef.current = null;

            formik.setFieldValue(`image_file.${eye}.image`, null);
            formik.setFieldValue(`image_file.${eye}.model_outputs`, {
                fundus: null, eyes: null, drdme: null,
            });
            modelOutputsRef.current[eye] = { fundus: null, eyes: null, drdme: null };

            if (eye === 'left') {
                teardownFilePondLeft();
                setLeftEye(null);
                leftEyeRef.current = null;
                setLeftGradable(undefined);
            } else {
                teardownFilePondRight();
                setRightEye(null);
                rightEyeRef.current = null;
                setRightGradable(undefined);
            }

            if (mobileSequentialOnnx) await gcYield(300);
        } catch (e) {
            /* teardown best-effort */
        }
    }, [formik, mobileSequentialOnnx, teardownFilePondLeft, teardownFilePondRight]);

    /** Mobile: full reload every 1 patient (2 eye pipelines) to reset JS heap + WASM.
     *  Without this, formik state and blob references accumulate across patients,
     *  pushing the JS heap from ~300 MB to 500+ MB per patient screened.
     *  Safari never returns freed WASM/decode memory to the OS, so a hard reload is the
     *  only way to fully reclaim it — done per-patient here to stay under the iOS jetsam ceiling. */
    const MOBILE_FULL_RELOAD_AFTER_SUCCESSFUL_PIPELINES = 2;
    const mobileSuccessfulPipelineCountRef = useRef(0);
    const leftFileInputRef = useRef(null);
    const rightFileInputRef = useRef(null);
    // Track latest model_outputs for each eye to avoid async state issues
    const modelOutputsRef = useRef({ left: { fundus: null, eyes: null, drdme: null }, right: { fundus: null, eyes: null, drdme: null } });
    // Refs to track if we're in "proceed" mode (to prevent formik sync from clearing)
    const leftEyeProceededRef = useRef(false);
    const rightEyeProceededRef = useRef(false);
    const location = useLocation();
    
    const handleDisabledProceed = () => {
        if(singleImageMode) {
            // In single image mode, only one eye needs to be uploaded AND reason must be selected
            if(((leftEye && leftGradable) || (rightEye && rightGradable)) && selectedSingleImageReason) {
                setDisabledProceed(false);
            } else {
                setDisabledProceed(true);
            }
        } else {
            // Normal mode - both eyes required
            if(leftEye && rightEye && leftGradable && rightGradable) {
                setDisabledProceed(false);
            } else {
                setDisabledProceed(true);
            }
        }
    };

    // Complete image validation and processing flow
    const handleImageUpload = async (file, eye) => {
        let tensorId = null; // worker-side cached preprocessed tensor
        try {
            // Set processing state and show loading screen
            setIsProcessing(true);
            currentProcessingFileRef.current = file;
            setCurrentProcessingEye(eye);
            setShowModelsUnavailableError(false);
            setModelsUnavailableMessage('');
            setModelsErrorDialogTitle('Models not available');
            failedDueToModelsRef.current = false;
            modelPrecheckFailedRef.current = false;

            // STEP 0: Ensure all required models are available.
            // Phones: OPFS existence only — no pinning three WASM heaps at once.
            // Desktop: load all three sessions upfront if missing.
            setProcessingMessage("Checking AI models...");
            setProcessingSubMessage("Please wait");

            // fundusSess / drdmeSess / eyesSess kept for mobile load+release pattern;
            // they resolve to WORKER_SENTINEL (truthy) when loaded — not used as session objects.
            let drdmeSess = null;
            let fundusSess = null;
            let eyesSess = null;

            if (mobileSequentialOnnx) {
                // Check models exist in storage — no WASM allocation yet.
                const [drdmeExists, fundusExists, eyesExists] = await Promise.all([
                    checkModelExists(),
                    checkFundusModelExists(),
                    checkEyesModelExists(),
                ]);
                if (!drdmeExists || !fundusExists || !eyesExists) {
                    const missing = [];
                    if (!drdmeExists) missing.push('DRDME');
                    if (!fundusExists) missing.push('Fundus detector');
                    if (!eyesExists) missing.push('Eyes detector');
                    setIsProcessing(false);
                    setModelsErrorDialogTitle('Models not available');
                    setModelsUnavailableMessage(`Required AI models are not available (missing: ${missing.join(', ')}). Please connect to the internet, then log out and log back in to download the models. You cannot proceed with screening until they are installed.`);
                    setShowModelsUnavailableError(true);
                    failedDueToModelsRef.current = true;
                    modelPrecheckFailedRef.current = true;
                    return { ok: false };
                }
            } else {
                // Non-iOS: ensure all models are loaded into the worker.
                if (!session)       drdmeSess   = await loadModel();       else drdmeSess   = session;
                if (!fundusSession) fundusSess   = await loadFundusModel(); else fundusSess  = fundusSession;
                if (!eyesSession)   eyesSess     = await loadEyesModel();  else eyesSess    = eyesSession;
                if (!drdmeSess || !fundusSess || !eyesSess) {
                    const missing = [];
                    if (!drdmeSess)  missing.push('DRDME');
                    if (!fundusSess) missing.push('Fundus detector');
                    if (!eyesSess)   missing.push('Eyes detector');
                    setIsProcessing(false);
                    setModelsErrorDialogTitle('Models not available');
                    setModelsUnavailableMessage(`Required AI models are not available (missing: ${missing.join(', ')}). Please connect to the internet, then log out and log back in to download the models. You cannot proceed with screening until they are installed.`);
                    setShowModelsUnavailableError(true);
                    failedDueToModelsRef.current = true;
                    modelPrecheckFailedRef.current = true;
                    return { ok: false };
                }
            }
            failedDueToModelsRef.current = false;
            modelPrecheckFailedRef.current = false;
            
            // STEP 1: Show "Uploading image..." screen
            setProcessingMessage("Uploading image...");
            setProcessingSubMessage("This may take a few minutes");
            
            // Small delay to show the uploading message
            await new Promise(resolve => setTimeout(resolve, 500));

            // DICOM → JPEG via Cornerstone wadouri + embedded-JPEG fallback (src/utils/dicomToImage.js).
            if (await isDicomCandidate(file)) {
                setProcessingMessage("Converting DICOM...");
                setProcessingSubMessage("Preparing image for analysis");
                try {
                    file = await convertDicomToImageFile(file);
                } catch (dicomErr) {
                    setIsProcessing(false);
                    const detail = formatDicomConversionError(dicomErr);
                    const short =
                        detail.length > 220 ? `${detail.slice(0, 220)}…` : detail;
                    setModelsErrorDialogTitle("Could not read this DICOM file");
                    setModelsUnavailableMessage(
                        `We could not convert this file to an image for analysis.\n\n${short}\n\nTry a JPEG or PNG from your device, or export an image from your DICOM software and upload that instead.`
                    );
                    setShowModelsUnavailableError(true);
                    return { ok: false };
                }
            }
            
            // STEP 2: Check for duplicate image
            setProcessingMessage("Checking for duplicate image...");
            setProcessingSubMessage("This may take a few minutes");
            
            // First check if the same image is already selected for the other eye in current session
            const otherEye = eye === "left" ? "right" : "left";
            const otherEyeImage = otherEye === "left" ? leftEyeRef.current : rightEyeRef.current;
            
            if (otherEyeImage) {
                const otherFile = Array.isArray(otherEyeImage) ? otherEyeImage[0].file : otherEyeImage;
                
                if (otherFile) {
                    try {
                        const currentFileHash = await getCachedImageHash(file);
                        const otherFileHash = await getCachedImageHash(otherFile);
                        
                        if (currentFileHash === otherFileHash) {
                            setIsProcessing(false);
                            setShowDuplicateError(true);
                            pendingImageRef.current = file;
                            setPendingEye(eye);
                            return { ok: false };
                        }
                    } catch (error) {
                        if (file.name === otherFile.name && file.size === otherFile.size) {
                            setIsProcessing(false);
                            setShowDuplicateError(true);
                            pendingImageRef.current = file;
                            setPendingEye(eye);
                            return { ok: false };
                        }
                    }
                }
            }
            
            // Check database for duplicates
            const duplicateResult = await checkImageDuplicates(file);
            
            if (duplicateResult.isDuplicate) {
                setIsProcessing(false);
                setShowDuplicateError(true);
                setDuplicateInfo(duplicateResult);
                pendingImageRef.current = file;
                setPendingEye(eye);
                return { ok: false };
            }
            
            // STEP 3–6: preprocess (OpenCV on main thread), then run each model in worker.
            setProcessingMessage("Checking for valid retinal image...");
            setProcessingSubMessage("This may take a few minutes");

            tensorId = `${eye}-${Date.now()}`;
            await preprocessAndCacheTensor(onnxClient, tensorId, file);
            if (mobileSequentialOnnx) await gcYield();

            // STEP 4: fundus
            if (mobileSequentialOnnx) {
                fundusSess = await loadFundusModel();
                if (!fundusSess) throw new Error('Fundus model could not be loaded');
            }
            const { result: fundusResult } = await onnxClient.runModel('fundus', tensorId);
            if (mobileSequentialOnnx) {
                releaseFundusSession();
                await gcYield(1200);
            }

            // Store fundus result in formik for later use
            const fundusOutput = {
                fundus_score: Array.isArray(fundusResult.fundus_score) ? [...fundusResult.fundus_score] : fundusResult.fundus_score,
                fundus_label: fundusResult.fundus_label,
                probability_non_fundus: fundusResult.fundus_score[0],
                probability_fundus: fundusResult.fundus_score[1],
                logit_non_fundus: fundusResult.fundus_logits?.[0] ?? null,
                logit_fundus: fundusResult.fundus_logits?.[1] ?? null
            };
            // Ensure model_outputs object exists before setting nested value
            // Use ref to track latest values to avoid async state issues
            modelOutputsRef.current[eye] = {
                ...modelOutputsRef.current[eye],
                fundus: fundusOutput
            };
            formik.setFieldValue(`image_file.${eye}.model_outputs`, {
                ...modelOutputsRef.current[eye]
            });

            // Check if fundus
            if (fundusResult.fundus_label !== 'fundus') {
                const newNonFundusCount = bumpSessionNonFundusCountForEye(eye);
                setIsSimilarityScoreIssue(false);

                if (newNonFundusCount >= 3) {
                    // Attempt 3+: keep file reference so user can proceed.
                    pendingImageRef.current = file;
                    // tensorId freed in finally
                } else {
                    await clearRejectedEyeState(eye);
                }

                setIsProcessing(false);
                setShowNonFundusError(true);
                setPendingEye(eye);
                return { ok: false };
            }

            // STEP 5: DRDME
            if (mobileSequentialOnnx) {
                drdmeSess = await loadModel();
                if (!drdmeSess) throw new Error('DRDME model could not be loaded');
            }
            if (drdmeSess) {
                const { result: drdmeResult } = await onnxClient.runModel('drdme', tensorId);
                if (mobileSequentialOnnx) await gcYield();
                
                // Store DRDME result in formik
                const drdmeOutput = {
                    DR_score: drdmeResult.DR_score,
                    DR_grade: drdmeResult.DR_grade,
                    DME_score: drdmeResult.DME_score,
                    DME_grade: drdmeResult.DME_grade,
                    similarity_score: drdmeResult.mahalanobis_distance,
                    features_available: drdmeResult.features !== null && drdmeResult.features !== undefined
                };
                // Ensure model_outputs object exists before setting nested value
                // Use ref to track latest values to avoid async state issues
                modelOutputsRef.current[eye] = {
                    ...modelOutputsRef.current[eye],
                    drdme: drdmeOutput
                };
                formik.setFieldValue(`image_file.${eye}.model_outputs`, {
                    ...modelOutputsRef.current[eye]
                });

                // Similarity / Mahalanobis: same 3-attempt flow per eye as non-fundus (1–2 red, 3rd yellow + referral)
                if (drdmeResult.mahalanobis_distance !== null && drdmeResult.mahalanobis_distance !== undefined && drdmeResult.mahalanobis_distance >= 80) {
                    if (mobileSequentialOnnx) await gcYield(1200);

                    const newNonFundusCount = bumpSessionNonFundusCountForEye(eye);
                    setIsSimilarityScoreIssue(true);

                    if (newNonFundusCount >= 3) {
                        pendingImageRef.current = file;
                        // tensorId freed in finally
                    } else {
                        await clearRejectedEyeState(eye);
                    }

                    setIsProcessing(false);
                    setShowNonFundusError(true);
                    setPendingEye(eye);
                    return { ok: false };
                }
            }
            if (mobileSequentialOnnx) await gcYield(400);

            // STEP 6: eyes
            if (mobileSequentialOnnx) {
                eyesSess = await loadEyesModel();
                if (!eyesSess) throw new Error('Eyes model could not be loaded');
            }
            if (eyesSess) {
                const { result: eyesResult } = await onnxClient.runModel('eyes', tensorId);
                if (mobileSequentialOnnx) await gcYield();

                // Store eyes result in formik
                const eyesOutput = {
                    eyes_score: Array.isArray(eyesResult.eyes_score) ? [...eyesResult.eyes_score] : eyesResult.eyes_score,
                    eyes_label: eyesResult.eyes_label,
                    probability_left_eye: eyesResult.eyes_score[0],
                    probability_right_eye: eyesResult.eyes_score[1],
                    logit_left_eye: eyesResult.eyes_logits?.[0] ?? null,
                    logit_right_eye: eyesResult.eyes_logits?.[1] ?? null
                };
                // Ensure model_outputs object exists before setting nested value
                // Use ref to track latest values to avoid async state issues
                modelOutputsRef.current[eye] = {
                    ...modelOutputsRef.current[eye],
                    eyes: eyesOutput
                };
                formik.setFieldValue(`image_file.${eye}.model_outputs`, {
                    ...modelOutputsRef.current[eye]
                });

                // Check if detected eye matches expected eye
                const expectedEyeLabel = eye === "left" ? "left_eye" : "right_eye";
                if (eyesResult.eyes_label !== expectedEyeLabel) {
                    if (mobileSequentialOnnx) {
                        releaseEyesSession();
                        await gcYield(1200);
                    }
                    setIsProcessing(false);
                    setShowWrongEyeError(true);
                    setWrongEyeInfo({
                        detectedEye: eyesResult.eyes_label,
                        expectedEye: eye
                    });
                    pendingImageRef.current = file;
                    setPendingEye(eye);
                    return { ok: false };
                }
            }
            if (mobileSequentialOnnx) {
                releaseEyesSession();
                await gcYield(1200);
            }

            // Valid fundus image accepted for this eye — new non-fundus cycle starts at 1 next time
            resetNonFundusAttemptStateForEye(eye);

            if (mobileSequentialOnnx) {
                try {
                    if (eye === 'left') teardownFilePondLeft();
                    if (eye === 'right') teardownFilePondRight();
                    if (eye === 'left') {
                        setLeftEye(null);
                        leftEyeRef.current = null;
                    } else {
                        setRightEye(null);
                        rightEyeRef.current = null;
                    }
                } catch (_) { /* ignore */ }
                mobileSuccessfulPipelineCountRef.current += 1;
                if (mobileSuccessfulPipelineCountRef.current >= MOBILE_FULL_RELOAD_AFTER_SUCCESSFUL_PIPELINES) {
                    mobileSuccessfulPipelineCountRef.current = 0;
                    setTimeout(() => {
                        window.location.reload();
                    }, 400);
                }
            }

            setIsProcessing(false);
            return { ok: true, processedFile: file };
            
        } catch (error) {
            setIsProcessing(false);
            const raw = error?.message || '';
            const modelMsg = getModelsUnavailableUserMessage(raw);
            modelPrecheckFailedRef.current = false;
            if (modelMsg) {
                setModelsErrorDialogTitle('Models not available');
                setModelsUnavailableMessage(modelMsg);
                failedDueToModelsRef.current = true;
            } else {
                setModelsErrorDialogTitle(GENERIC_PROCESSING_ERROR_TITLE);
                setModelsUnavailableMessage(raw || 'Image processing failed. Please try again.');
                failedDueToModelsRef.current = false;
            }
            setShowModelsUnavailableError(true);
            return { ok: false };
        } finally {
            if (tensorId) onnxClient.clearTensor(tensorId).catch(() => {});
            if (mobileSequentialOnnx) {
                await releaseMobileDrdmeIfNeeded();
            }
            currentProcessingFileRef.current = null;
        }
    };

    // Handle duplicate warning cancellation (legacy - kept for backward compatibility)
    const handleDuplicateCancel = () => {
        // This function is kept for backward compatibility but is no longer used
        // The new flow uses handleDuplicateErrorReupload instead
    };
    
    // Handle duplicate error reupload
    const handleDuplicateErrorReupload = () => {
        setShowDuplicateError(false);
        setDuplicateInfo(null);
        const eyeToProcess = currentProcessingEye;
        
        // Clear the image state immediately when user clicks re-upload
        if (eyeToProcess === "left") {
            setLeftEye(null);
            leftEyeRef.current = null;
            setLeftGradable(undefined);
            formik.setFieldValue("image_file.left.image", null);
            // Clear model_outputs when user reuploads - old ML data shouldn't persist
            formik.setFieldValue("image_file.left.model_outputs", { fundus: null, eyes: null, drdme: null });
            modelOutputsRef.current.left = { fundus: null, eyes: null, drdme: null };
            teardownFilePondLeft();
        } else if (eyeToProcess === "right") {
            setRightEye(null);
            rightEyeRef.current = null;
            setRightGradable(undefined);
            formik.setFieldValue("image_file.right.image", null);
            // Clear model_outputs when user reuploads - old ML data shouldn't persist
            formik.setFieldValue("image_file.right.model_outputs", { fundus: null, eyes: null, drdme: null });
            modelOutputsRef.current.right = { fundus: null, eyes: null, drdme: null };
            teardownFilePondRight();
        }
        
        // Clear pending state
        pendingImageRef.current = null;
        setPendingEye(null);
        setIsProcessing(false);
        
        // Trigger file selection
        if (eyeToProcess === "left" && leftFileInputRef.current) {
            leftFileInputRef.current.click();
        } else if (eyeToProcess === "right" && rightFileInputRef.current) {
            rightFileInputRef.current.click();
        }
    };
    
    // Handle non-fundus error reupload
    const handleNonFundusErrorReupload = () => {
        setShowNonFundusError(false);
        setIsSimilarityScoreIssue(false);
        // Drop cached heavy refs — user chose to re-upload, not proceed.
        pendingImageRef.current = null;
        try { pendingTensorRef.current?.fill?.(0); } catch (_) {}
        pendingTensorRef.current = null;
        const eyeToProcess = currentProcessingEye;
        
        // Clear the image state immediately when user clicks re-upload
        if (eyeToProcess === "left") {
            setLeftEye(null);
            leftEyeRef.current = null;
            setLeftGradable(undefined);
            formik.setFieldValue("image_file.left.image", null);
            // Clear model_outputs when user reuploads - old ML data shouldn't persist
            formik.setFieldValue("image_file.left.model_outputs", { fundus: null, eyes: null, drdme: null });
            modelOutputsRef.current.left = { fundus: null, eyes: null, drdme: null };
            teardownFilePondLeft();
        } else if (eyeToProcess === "right") {
            setRightEye(null);
            rightEyeRef.current = null;
            setRightGradable(undefined);
            formik.setFieldValue("image_file.right.image", null);
            // Clear model_outputs when user reuploads - old ML data shouldn't persist
            formik.setFieldValue("image_file.right.model_outputs", { fundus: null, eyes: null, drdme: null });
            modelOutputsRef.current.right = { fundus: null, eyes: null, drdme: null };
            teardownFilePondRight();
        }
        
        // Clear pending state
        pendingImageRef.current = null;
        setPendingEye(null);
        setIsProcessing(false);
        
        // Trigger file selection
        if (eyeToProcess === "left" && leftFileInputRef.current) {
            leftFileInputRef.current.click();
        } else if (eyeToProcess === "right" && rightFileInputRef.current) {
            rightFileInputRef.current.click();
        }
    };
    
    // Handle non-fundus error proceed (on 3rd attempt)
    const handleNonFundusErrorProceed = async () => {
        setShowNonFundusError(false);
        setIsSimilarityScoreIssue(false); // Reset similarity score flag on proceed
        const eyeToProceed = currentProcessingEye;
        
        const imageToProceed = pendingImageRef.current;
        
        if (!imageToProceed) {
            setIsProcessing(false);
            return;
        }
        
        // Mark operator override for fundus (user proceeded despite non-fundus warning)
        formik.setFieldValue(`image_file.${eyeToProceed}.operator_override_fundus`, true);
        
        // Show processing screen - just show "Uploading image..." for the entire process
        setIsProcessing(true);
        setProcessingMessage("Uploading image...");
        setProcessingSubMessage("This may take a few minutes");

        pendingTensorRef.current = null;
        pendingImageRef.current = null;

        let proceedTensorId = null;
        try {
            proceedTensorId = `proceed-${eyeToProceed}-${Date.now()}`;
            await preprocessAndCacheTensor(onnxClient, proceedTensorId, imageToProceed);
            if (mobileSequentialOnnx) await gcYield();

            // Get stored fundus result (from previous validation attempts)
            const storedFundus = formik.values.image_file[eyeToProceed]?.model_outputs?.fundus;
            const needFundusInference =
                !storedFundus?.fundus_label || storedFundus.fundus_label === 'fundus';
            if (needFundusInference) {
                let fundusSess = mobileSequentialOnnx ? await loadFundusModel() : (fundusSession || await loadFundusModel());
                if (fundusSess) {
                    const { result: fundusResult } = await onnxClient.runModel('fundus', proceedTensorId);
                    if (mobileSequentialOnnx) { releaseFundusSession(); await gcYield(); }
                    const fundusOutput = {
                        fundus_score: Array.isArray(fundusResult.fundus_score) ? [...fundusResult.fundus_score] : fundusResult.fundus_score,
                        fundus_label: fundusResult.fundus_label,
                        probability_non_fundus: fundusResult.fundus_score[0],
                        probability_fundus: fundusResult.fundus_score[1],
                        logit_non_fundus: fundusResult.fundus_logits?.[0] ?? null,
                        logit_fundus: fundusResult.fundus_logits?.[1] ?? null
                    };
                // Use ref to track latest values to avoid async state issues
                modelOutputsRef.current[eyeToProceed] = {
                    ...modelOutputsRef.current[eyeToProceed],
                    fundus: fundusOutput
                };
                formik.setFieldValue(`image_file.${eyeToProceed}.model_outputs`, {
                    ...modelOutputsRef.current[eyeToProceed]
                });
                }
            }
            
            // STEP 1: eyes
            let eyesSess = mobileSequentialOnnx ? await loadEyesModel() : (eyesSession || await loadEyesModel());
            
            if (eyesSess) {
                const { result: eyesResult } = await onnxClient.runModel('eyes', proceedTensorId);
                if (mobileSequentialOnnx) { releaseEyesSession(); await gcYield(); }
                
                const eyesOutput = {
                    eyes_score: Array.isArray(eyesResult.eyes_score) ? [...eyesResult.eyes_score] : eyesResult.eyes_score,
                    eyes_label: eyesResult.eyes_label,
                    probability_left_eye: eyesResult.eyes_score[0],
                    probability_right_eye: eyesResult.eyes_score[1],
                    logit_left_eye: eyesResult.eyes_logits?.[0] ?? null,
                    logit_right_eye: eyesResult.eyes_logits?.[1] ?? null
                };
                modelOutputsRef.current[eyeToProceed] = {
                    ...modelOutputsRef.current[eyeToProceed],
                    eyes: eyesOutput
                };
                formik.setFieldValue(`image_file.${eyeToProceed}.model_outputs`, {
                    ...modelOutputsRef.current[eyeToProceed]
                });
            }
            
            // STEP 2: DRDME
            let drdmeSess = mobileSequentialOnnx ? await loadModel() : (session || await loadModel());
            
            if (drdmeSess) {
                const { result: drdmeResult } = await onnxClient.runModel('drdme', proceedTensorId);
                if (mobileSequentialOnnx) await gcYield();
                
                const drdmeOutput = {
                    DR_score: drdmeResult.DR_score,
                    DR_grade: drdmeResult.DR_grade,
                    DME_score: drdmeResult.DME_score,
                    DME_grade: drdmeResult.DME_grade,
                    similarity_score: drdmeResult.mahalanobis_distance,
                    features_available: drdmeResult.features_available ?? false
                };
                modelOutputsRef.current[eyeToProceed] = {
                    ...modelOutputsRef.current[eyeToProceed],
                    drdme: drdmeOutput
                };
                formik.setFieldValue(`image_file.${eyeToProceed}.model_outputs`, {
                    ...modelOutputsRef.current[eyeToProceed]
                });
            }
            
            // Hide processing screen
            setIsProcessing(false);
            
            // Now set the image - CRITICAL: Keep array format for ImageUploaded component
            const fileItems = [{
                file: imageToProceed,
                id: `proceed-${Date.now()}-${Math.random()}`,
                status: 1,
                serverId: null
            }];
            
            if (eyeToProceed === "left") {
                // CRITICAL: Set ref FIRST (synchronous) to prevent formik sync useEffect from clearing
                leftEyeProceededRef.current = true;
                skipFormikSyncRef.current.left = true; // Prevent formik sync for a short time
                // Set ref immediately (synchronous)
                leftEyeRef.current = fileItems;
                // Set state - React will detect the new array reference and re-render
                setLeftGradable("proceeded");
                setLeftEyeProceeded(true);
                // Force state update by creating completely new array
                setLeftEye([...fileItems]); // Spread to create new array reference
                // Update formik
                formik.setFieldValue("image_file.left.image", fileItems);
                // Clear the skip flag after delay to ensure state has settled
                setTimeout(() => {
                    skipFormikSyncRef.current.left = false;
                }, 300);
            } else if (eyeToProceed === "right") {
                // CRITICAL: Set ref FIRST (synchronous) to prevent formik sync useEffect from clearing
                rightEyeProceededRef.current = true;
                skipFormikSyncRef.current.right = true; // Prevent formik sync for a short time
                // Set ref immediately (synchronous)
                rightEyeRef.current = fileItems;
                // Set state - React will detect the new array reference and re-render
                setRightGradable("proceeded");
                setRightEyeProceeded(true);
                // Force state update by creating completely new array
                setRightEye([...fileItems]); // Spread to create new array reference
                // Update formik
                formik.setFieldValue("image_file.right.image", fileItems);
                // Clear the skip flag after delay to ensure state has settled
                setTimeout(() => {
                    skipFormikSyncRef.current.right = false;
                }, 300);
            }

            resetNonFundusAttemptStateForEye(eyeToProceed);
        } catch (error) {
            setIsProcessing(false);
            if (isModelUnavailableError(error)) {
                setModelsErrorDialogTitle('Models not available');
                setModelsUnavailableMessage(getModelsUnavailableUserMessage(error?.message) || MODELS_UNAVAILABLE_MSG);
                setShowModelsUnavailableError(true);
                if (eyeToProceed === "left") {
                    setLeftEye(null);
                    leftEyeRef.current = null;
                    setLeftGradable(undefined);
                    formik.setFieldValue("image_file.left.image", null);
                    formik.setFieldValue("image_file.left.model_outputs", { fundus: null, eyes: null, drdme: null });
                    modelOutputsRef.current.left = { fundus: null, eyes: null, drdme: null };
                    teardownFilePondLeft();
                } else if (eyeToProceed === "right") {
                    setRightEye(null);
                    rightEyeRef.current = null;
                    setRightGradable(undefined);
                    formik.setFieldValue("image_file.right.image", null);
                    formik.setFieldValue("image_file.right.model_outputs", { fundus: null, eyes: null, drdme: null });
                    modelOutputsRef.current.right = { fundus: null, eyes: null, drdme: null };
                    teardownFilePondRight();
                }
            } else {
                const fileItems = [{ file: imageToProceed, id: Date.now().toString(), status: 1, serverId: null }];
                if (eyeToProceed === "left") {
                    leftEyeProceededRef.current = true;
                    skipFormikSyncRef.current.left = true;
                    setLeftGradable("proceeded");
                    setLeftEyeProceeded(true);
                    leftEyeRef.current = fileItems;
                    setLeftEye(() => fileItems);
                    formik.setFieldValue("image_file.left.image", fileItems);
                    setTimeout(() => { skipFormikSyncRef.current.left = false; }, 100);
                } else if (eyeToProceed === "right") {
                    rightEyeProceededRef.current = true;
                    skipFormikSyncRef.current.right = true;
                    setRightGradable("proceeded");
                    setRightEyeProceeded(true);
                    rightEyeRef.current = fileItems;
                    setRightEye(() => fileItems);
                    formik.setFieldValue("image_file.right.image", fileItems);
                    setTimeout(() => { skipFormikSyncRef.current.right = false; }, 100);
                }
                resetNonFundusAttemptStateForEye(eyeToProceed);
            }
        } finally {
            if (proceedTensorId) onnxClient.clearTensor(proceedTensorId).catch(() => {});
            if (mobileSequentialOnnx) {
                await releaseMobileDrdmeIfNeeded();
            }
        }
        
        pendingImageRef.current = null;
        setPendingEye(null);
    };
    
    // Handle wrong eye error reupload
    const handleWrongEyeErrorReupload = () => {
        setShowWrongEyeError(false);
        setWrongEyeInfo(null);
        const eyeToProcess = currentProcessingEye;
        
        // Clear the image state immediately when user clicks re-upload
        if (eyeToProcess === "left") {
            setLeftEye(null);
            leftEyeRef.current = null;
            setLeftGradable(undefined);
            formik.setFieldValue("image_file.left.image", null);
            // Clear model_outputs when user reuploads - old ML data shouldn't persist
            formik.setFieldValue("image_file.left.model_outputs", { fundus: null, eyes: null, drdme: null });
            modelOutputsRef.current.left = { fundus: null, eyes: null, drdme: null };
            teardownFilePondLeft();
        } else if (eyeToProcess === "right") {
            setRightEye(null);
            rightEyeRef.current = null;
            setRightGradable(undefined);
            formik.setFieldValue("image_file.right.image", null);
            // Clear model_outputs when user reuploads - old ML data shouldn't persist
            formik.setFieldValue("image_file.right.model_outputs", { fundus: null, eyes: null, drdme: null });
            modelOutputsRef.current.right = { fundus: null, eyes: null, drdme: null };
            teardownFilePondRight();
        }
        
        // Clear pending state
        pendingImageRef.current = null;
        setPendingEye(null);
        setIsProcessing(false);
        
        // Trigger file selection
        if (eyeToProcess === "left" && leftFileInputRef.current) {
            leftFileInputRef.current.click();
        } else if (eyeToProcess === "right" && rightFileInputRef.current) {
            rightFileInputRef.current.click();
        }
    };
    
    // Handle wrong eye error proceed
    const handleWrongEyeErrorProceed = async () => {
        setShowWrongEyeError(false);
        setWrongEyeInfo(null);
        
        const eyeToProceed = currentProcessingEye;
        
        const imageToProceed = pendingImageRef.current;
        
        if (!imageToProceed) {
            setIsProcessing(false);
            return;
        }
        
        // Mark operator override for eye (user proceeded despite wrong eye warning)
        formik.setFieldValue(`image_file.${eyeToProceed}.operator_override_eye`, true);
        
        // Show processing screen - just show "Uploading image..." for the entire process
        setIsProcessing(true);
        setProcessingMessage("Uploading image...");
        setProcessingSubMessage("This may take a few minutes");
        
        let wrongEyeTensorId = null;
        try {
            wrongEyeTensorId = `wrongeye-${eyeToProceed}-${Date.now()}`;
            await preprocessAndCacheTensor(onnxClient, wrongEyeTensorId, imageToProceed);
            if (mobileSequentialOnnx) await gcYield();

            // Get stored fundus result (from previous validation)
            const storedFundus = formik.values.image_file[eyeToProceed]?.model_outputs?.fundus;
            if (!storedFundus) {
                let fundusSess = mobileSequentialOnnx
                    ? await loadFundusModel()
                    : (fundusSession || (await loadFundusModel()));
                if (fundusSess) {
                    const { result: fundusResult } = await onnxClient.runModel('fundus', wrongEyeTensorId);
                    if (mobileSequentialOnnx) {
                        releaseFundusSession();
                        await gcYield();
                    }
                    const fundusOutput = {
                        fundus_score: Array.isArray(fundusResult.fundus_score) ? [...fundusResult.fundus_score] : fundusResult.fundus_score,
                        fundus_label: fundusResult.fundus_label,
                        probability_non_fundus: fundusResult.fundus_score[0],
                        probability_fundus: fundusResult.fundus_score[1],
                        logit_non_fundus: fundusResult.fundus_logits?.[0] ?? null,
                        logit_fundus: fundusResult.fundus_logits?.[1] ?? null
                    };
                // Use ref to track latest values to avoid async state issues
                modelOutputsRef.current[eyeToProceed] = {
                    ...modelOutputsRef.current[eyeToProceed],
                    fundus: fundusOutput
                };
                formik.setFieldValue(`image_file.${eyeToProceed}.model_outputs`, {
                    ...modelOutputsRef.current[eyeToProceed]
                });
                }
            }
            
            // STEP 1: eyes
            let eyesSessW = mobileSequentialOnnx ? await loadEyesModel() : (eyesSession || await loadEyesModel());
            
            if (eyesSessW) {
                const { result: eyesResult } = await onnxClient.runModel('eyes', wrongEyeTensorId);
                if (mobileSequentialOnnx) { releaseEyesSession(); await gcYield(); }
                
                const eyesOutput = {
                    eyes_score: Array.isArray(eyesResult.eyes_score) ? [...eyesResult.eyes_score] : eyesResult.eyes_score,
                    eyes_label: eyesResult.eyes_label,
                    probability_left_eye: eyesResult.eyes_score[0],
                    probability_right_eye: eyesResult.eyes_score[1],
                    logit_left_eye: eyesResult.eyes_logits?.[0] ?? null,
                    logit_right_eye: eyesResult.eyes_logits?.[1] ?? null
                };
                modelOutputsRef.current[eyeToProceed] = {
                    ...modelOutputsRef.current[eyeToProceed],
                    eyes: eyesOutput
                };
                formik.setFieldValue(`image_file.${eyeToProceed}.model_outputs`, {
                    ...modelOutputsRef.current[eyeToProceed]
                });
            }
            
            // STEP 2: DRDME
            let drdmeSessW = mobileSequentialOnnx ? await loadModel() : (session || await loadModel());
            
            if (drdmeSessW) {
                const { result: drdmeResult } = await onnxClient.runModel('drdme', wrongEyeTensorId);
                if (mobileSequentialOnnx) await gcYield();
                
                const drdmeOutput = {
                    DR_score: drdmeResult.DR_score,
                    DR_grade: drdmeResult.DR_grade,
                    DME_score: drdmeResult.DME_score,
                    DME_grade: drdmeResult.DME_grade,
                    similarity_score: drdmeResult.mahalanobis_distance,
                    features_available: drdmeResult.features_available ?? false
                };
                modelOutputsRef.current[eyeToProceed] = {
                    ...modelOutputsRef.current[eyeToProceed],
                    drdme: drdmeOutput
                };
                formik.setFieldValue(`image_file.${eyeToProceed}.model_outputs`, {
                    ...modelOutputsRef.current[eyeToProceed]
                });
            }
            
            // Hide processing screen
            setIsProcessing(false);
            
            // Now set the image - CRITICAL: Keep array format for ImageUploaded component
            const fileItems = [{
                file: imageToProceed,
                id: Date.now().toString(),
                status: 1,
                serverId: null
            }];
            
            if (eyeToProceed === "left") {
                // CRITICAL: Set ref FIRST (synchronous) to prevent formik sync useEffect from clearing
                leftEyeProceededRef.current = true;
                skipFormikSyncRef.current.left = true;
                // Then set state (React batches these)
                setLeftGradable("proceeded"); // Special status for proceeded images
                setLeftEyeProceeded(true); // Mark as proceeded for yellow status
                leftEyeRef.current = fileItems;
                setLeftEye(() => fileItems); // Functional update prevents stale closures
                // Update formik IMMEDIATELY to prevent sync useEffect from clearing it with null
                // CRITICAL: Also set formik with array format to keep consistency
                formik.setFieldValue("image_file.left.image", fileItems);
                setTimeout(() => {
                    skipFormikSyncRef.current.left = false;
                }, 100);
            } else if (eyeToProceed === "right") {
                // CRITICAL: Set ref FIRST (synchronous) to prevent formik sync useEffect from clearing
                rightEyeProceededRef.current = true;
                skipFormikSyncRef.current.right = true;
                // Then set state (React batches these)
                setRightGradable("proceeded"); // Special status for proceeded images
                setRightEyeProceeded(true); // Mark as proceeded for yellow status
                rightEyeRef.current = fileItems;
                setRightEye(() => fileItems); // Functional update prevents stale closures
                // Update formik IMMEDIATELY to prevent sync useEffect from clearing it with null
                // CRITICAL: Also set formik with array format to keep consistency
                formik.setFieldValue("image_file.right.image", fileItems);
                setTimeout(() => {
                    skipFormikSyncRef.current.right = false;
                }, 100);
            }
        } catch (error) {
            setIsProcessing(false);
            if (isModelUnavailableError(error)) {
                setModelsErrorDialogTitle('Models not available');
                setModelsUnavailableMessage(getModelsUnavailableUserMessage(error?.message) || MODELS_UNAVAILABLE_MSG);
                setShowModelsUnavailableError(true);
                if (eyeToProceed === "left") {
                    setLeftEye(null);
                    leftEyeRef.current = null;
                    setLeftGradable(undefined);
                    formik.setFieldValue("image_file.left.image", null);
                    formik.setFieldValue("image_file.left.model_outputs", { fundus: null, eyes: null, drdme: null });
                    modelOutputsRef.current.left = { fundus: null, eyes: null, drdme: null };
                    teardownFilePondLeft();
                } else if (eyeToProceed === "right") {
                    setRightEye(null);
                    rightEyeRef.current = null;
                    setRightGradable(undefined);
                    formik.setFieldValue("image_file.right.image", null);
                    formik.setFieldValue("image_file.right.model_outputs", { fundus: null, eyes: null, drdme: null });
                    modelOutputsRef.current.right = { fundus: null, eyes: null, drdme: null };
                    teardownFilePondRight();
                }
            } else {
                const fileItems = [{ file: imageToProceed, id: Date.now().toString(), status: 1, serverId: null }];
                if (eyeToProceed === "left") {
                    leftEyeProceededRef.current = true;
                    skipFormikSyncRef.current.left = true;
                    setLeftGradable("proceeded");
                    setLeftEyeProceeded(true);
                    leftEyeRef.current = fileItems;
                    setLeftEye(() => fileItems);
                    formik.setFieldValue("image_file.left.image", fileItems);
                    setTimeout(() => { skipFormikSyncRef.current.left = false; }, 100);
                } else if (eyeToProceed === "right") {
                    rightEyeProceededRef.current = true;
                    skipFormikSyncRef.current.right = true;
                    setRightGradable("proceeded");
                    setRightEyeProceeded(true);
                    rightEyeRef.current = fileItems;
                    setRightEye(() => fileItems);
                    formik.setFieldValue("image_file.right.image", fileItems);
                    setTimeout(() => { skipFormikSyncRef.current.right = false; }, 100);
                }
            }
        } finally {
            if (wrongEyeTensorId) onnxClient.clearTensor(wrongEyeTensorId).catch(() => {});
            if (mobileSequentialOnnx) {
                await releaseMobileDrdmeIfNeeded();
            }
        }
        
        pendingImageRef.current = null;
        setPendingEye(null);
    };
    
    useEffect(() => {
        window.history.pushState(null, "", location.pathname);
        const handlePopState = (event) => {
            handleBack();
        };
      
        window.addEventListener("popstate", handlePopState);
      
        return () => {
          window.removeEventListener("popstate", handlePopState);
        };
      }, []);

    useEffect(() => {handleDisabledProceed()}, [leftEye, rightEye,leftGradable,rightGradable, singleImageMode, selectedSingleImageReason]);

    // When both eyes have no image in formik and local state, clear pond validation ids.
    // Do NOT reset sessionNonFundusCount here: attempts 1–2 call clearRejectedEyeState (empty images),
    // and this effect would fire and wipe the per-eye counter so every reject looked like attempt 1.
    // Counters reset via resetNonFundusAttemptStateForEye on success/proceed, or ImageUpload remount (e.g. inference → add patient).
    useEffect(() => {
        const leftImg = formik.values.image_file?.left?.image;
        const rightImg = formik.values.image_file?.right?.image;
        const hasFormikLeft = leftImg != null && leftImg !== "";
        const hasFormikRight = rightImg != null && rightImg !== "";
        if (hasFormikLeft || hasFormikRight) return;
        if (leftEye || rightEye) return;
        lastValidatedPondItemIdRef.current.left = null;
        lastValidatedPondItemIdRef.current.right = null;
    }, [formik.values.image_file?.left?.image, formik.values.image_file?.right?.image, leftEye, rightEye]);
    
    // Sync with formik values when they change (for edit mode)
    useEffect(() => {
        // Don't overwrite if gradable is "proceeded" - preserve array format
        // Also check if current state is already an array (for proceeded images)
        // Use refs to check immediately (React batches state updates)
        const leftIsArray = Array.isArray(leftEye);
        const rightIsArray = Array.isArray(rightEye);
        
        // CRITICAL: Check refs FIRST - they're synchronous and prevent clearing proceeded images
        const leftProceeded = leftEyeProceededRef.current || leftGradable === "proceeded" || leftIsArray;
        const rightProceeded = rightEyeProceededRef.current || rightGradable === "proceeded" || rightIsArray;
        
        // Skip formik sync if we just set the image (prevent race conditions)
        if (skipFormikSyncRef.current.left || skipFormikSyncRef.current.right) {
            return;
        }
        
        // CRITICAL: Don't sync if state is array OR gradable is "proceeded" OR ref indicates proceeded - prevents overwriting proceeded images
        // Also check if both are arrays with same file content to avoid unnecessary syncing
        const formikLeftIsArray = Array.isArray(formik.values.image_file.left.image);
        const leftEyeIsArray = Array.isArray(leftEye);
        const leftArraysMatch = formikLeftIsArray && leftEyeIsArray && 
            formik.values.image_file.left.image.length > 0 && leftEye.length > 0 &&
            formik.values.image_file.left.image[0]?.file === leftEye[0]?.file;
        
        // CRITICAL: If both are arrays and they're the same reference OR have the same file, don't sync
        const leftIsSameArray = formikLeftIsArray && leftEyeIsArray && 
            (formik.values.image_file.left.image === leftEye || leftArraysMatch);
        
        if (isValidImageData(formik.values.image_file.left.image) && formik.values.image_file.left.image !== leftEye && !leftProceeded && !leftIsSameArray) {
            setLeftEye(formik.values.image_file.left.image);
            // Set gradability state if available
            if (formik.values.image_file.left.gradabale) {
                setLeftGradable(formik.values.image_file.left.gradabale);
            } else if (formik.values.image_file.left.classification && Object.keys(formik.values.image_file.left.classification).length > 0) {
                // If classification exists, image was already validated - set to "good"
                setLeftGradable("good");
            } else if (formik.values.image_file.left.model_outputs && (formik.values.image_file.left.model_outputs.fundus || formik.values.image_file.left.model_outputs.drdme || formik.values.image_file.left.model_outputs.eyes)) {
                // If model outputs exist, image was already processed - set to "good"
                setLeftGradable("good");
            }
        } else if (!isValidImageData(formik.values.image_file.left.image) && leftEye !== null) {
            if (!leftProceeded) {
                setLeftEye(null);
                leftEyeRef.current = null;
            }
        }
        
        // CRITICAL: Don't sync if state is array OR gradable is "proceeded" OR ref indicates proceeded - prevents overwriting proceeded images
        // Also check if both are arrays with same file content to avoid unnecessary syncing
        const formikRightIsArray = Array.isArray(formik.values.image_file.right.image);
        const rightEyeIsArray = Array.isArray(rightEye);
        const rightArraysMatch = formikRightIsArray && rightEyeIsArray && 
            formik.values.image_file.right.image.length > 0 && rightEye.length > 0 &&
            formik.values.image_file.right.image[0]?.file === rightEye[0]?.file;
        
        // CRITICAL: If both are arrays and they're the same reference OR have the same file, don't sync
        const rightIsSameArray = formikRightIsArray && rightEyeIsArray && 
            (formik.values.image_file.right.image === rightEye || rightArraysMatch);
        
        if (isValidImageData(formik.values.image_file.right.image) && formik.values.image_file.right.image !== rightEye && !rightProceeded && !rightIsSameArray) {
            setRightEye(formik.values.image_file.right.image);
            // Set gradability state if available
            if (formik.values.image_file.right.gradabale) {
                setRightGradable(formik.values.image_file.right.gradabale);
            } else if (formik.values.image_file.right.classification && Object.keys(formik.values.image_file.right.classification).length > 0) {
                // If classification exists, image was already validated - set to "good"
                setRightGradable("good");
            } else if (formik.values.image_file.right.model_outputs && (formik.values.image_file.right.model_outputs.fundus || formik.values.image_file.right.model_outputs.drdme || formik.values.image_file.right.model_outputs.eyes)) {
                // If model outputs exist, image was already processed - set to "good"
                setRightGradable("good");
            }
        } else if (!isValidImageData(formik.values.image_file.right.image) && rightEye !== null) {
            if (!rightProceeded) {
                setRightEye(null);
                rightEyeRef.current = null;
            }
        }
        // Set single image mode if there's a reason
        if (formik.values.image_file.single_image_reason) {
            setSingleImageMode(true);
            setSelectedSingleImageReason(formik.values.image_file.single_image_reason);
        }
        
        // If images are already loaded but gradability is not set, check if they were already validated
        if (leftEye && !leftGradable) {
            if (formik.values.image_file.left.classification && Object.keys(formik.values.image_file.left.classification).length > 0) {
                setLeftGradable("good");
            } else if (formik.values.image_file.left.model_outputs && (formik.values.image_file.left.model_outputs.fundus || formik.values.image_file.left.model_outputs.drdme || formik.values.image_file.left.model_outputs.eyes)) {
                setLeftGradable("good");
            }
        }
        
        if (rightEye && !rightGradable) {
            if (formik.values.image_file.right.classification && Object.keys(formik.values.image_file.right.classification).length > 0) {
                setRightGradable("good");
            } else if (formik.values.image_file.right.model_outputs && (formik.values.image_file.right.model_outputs.fundus || formik.values.image_file.right.model_outputs.drdme || formik.values.image_file.right.model_outputs.eyes)) {
                setRightGradable("good");
            }
        }
    }, [formik.values.image_file.left.image, formik.values.image_file.right.image, formik.values.image_file.single_image_reason, formik.values.image_file.left.classification, formik.values.image_file.right.classification, formik.values.image_file.left.model_outputs, formik.values.image_file.right.model_outputs, leftEye, rightEye, leftGradable, rightGradable]);
    
    // Auto-detect and handle single image mode conflicts
    useEffect(() => {
        if (singleImageMode && leftEye && rightEye) {
            // Both images exist but single mode is checked - auto-disable single mode
            setSingleImageMode(false);
            setSelectedSingleImageReason(''); // Clear the reason
            setShowSingleImageWarning(true);
            
            // Auto-hide warning after 3 seconds
            setTimeout(() => {
                setShowSingleImageWarning(false);
            }, 3000);
        }
    }, [leftEye, rightEye, singleImageMode]);
    useEffect(() => {
        // Only run if:
        // 1. Image exists
        // 2. Confirmation dialog is closed
        // 3. Gradable is NOT "proceeded" (user didn't proceed with warnings)
        // 4. Gradable is NOT already set to "good" (prevent showing "Great job!" immediately)
        // This prevents showing "Great job!" immediately when selecting an image
        // It should only show after validation passes
        if(leftEye !== null && showLeftImageConfirmation === false && leftGradable !== "proceeded" && leftGradable !== "good") {
            // DON'T call handleImageValueSet - it changes format from array to File
            // Only update formik if needed, but preserve array format
            // handleImageValueSet(leftEye, "left", formik); // REMOVED - preserves array format
            
            // Don't set to "good" here - only set after validation passes
            // setLeftGradable("good"); // REMOVED - only set after validation
        }
        // Don't clear formik values when leftEye is null - this was overwriting existing data!
    },[leftEye, showLeftImageConfirmation, leftGradable]);

    useEffect(() => {
        // Only run if:
        // 1. Image exists
        // 2. Confirmation dialog is closed
        // 3. Gradable is NOT "proceeded" (user didn't proceed with warnings)
        // 4. Gradable is NOT already set to "good" (prevent showing "Great job!" immediately)
        // This prevents showing "Great job!" immediately when selecting an image
        // It should only show after validation passes
        if(rightEye && showRightImageConfirmation === false && rightGradable !== "proceeded" && rightGradable !== "good") {
            // DON'T call handleImageValueSet - it changes format from array to File
            // Only update formik if needed, but preserve array format
            // handleImageValueSet(rightEye, "right", formik); // REMOVED - preserves array format
            
            // Don't set to "good" here - only set after validation passes
            // setRightGradable("good"); // REMOVED - only set after validation
        }
        // Don't clear formik values when rightEye is null - this was overwriting existing data!
    }, [rightEye, showRightImageConfirmation, rightGradable]);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const handleProceed = () => {
        // Update formik with image files before submitting
        // Extract File objects from arrays if needed (for proceeded images)
        if (leftEye) {
            const leftFile = Array.isArray(leftEye) && leftEye.length > 0 ? leftEye[0].file : leftEye;
            if (leftFile instanceof File) {
                formik.setFieldValue("image_file.left.image", leftFile);
            }
        }
        if (rightEye) {
            const rightFile = Array.isArray(rightEye) && rightEye.length > 0 ? rightEye[0].file : rightEye;
            if (rightFile instanceof File) {
                formik.setFieldValue("image_file.right.image", rightFile);
            }
        }
        
        if(singleImageMode) {
            // In single image mode, check if reason is selected
            if(!selectedSingleImageReason) {
                return; // Don't proceed if reason not selected
            }
            if(setActiveStep) {
                setActiveStep(2);
            } else {
                handleSubmit();
            }
            return;
        }

        if(leftGradable === "bad") {
            setProceedNotLeftGradable(true);
        }
        if(rightGradable === "bad") {
            setProceedNotRightGradable(true);
        }
        // Accept both "good" and "proceeded" status
        const leftValid = leftGradable === "good" || leftGradable === "proceeded";
        const rightValid = rightGradable === "good" || rightGradable === "proceeded";
        if(leftValid && rightValid) {
            if(setActiveStep) {
                setActiveStep(2);
            } else {
                handleSubmit();
            }
        }
    };

    const handleSingleImageReasonChange = (event) => {
        setSelectedSingleImageReason(event.target.value);
        formik.setFieldValue("image_file.single_image_reason", event.target.value);
    };


    const handleResetLeftEye = (value) => {
        setLeftEye(value);
        leftEyeRef.current = value;
        if (value === null) {
            formik.setFieldValue("image_file.left.image", null);
            // Clear model_outputs when user resets image - old ML data shouldn't persist
            formik.setFieldValue("image_file.left.model_outputs", { fundus: null, eyes: null, drdme: null });
            modelOutputsRef.current.left = { fundus: null, eyes: null, drdme: null };
            setLeftGradable(undefined);
            setLeftEyeProceeded(false);
            leftEyeProceededRef.current = false; // Reset ref
            teardownFilePondLeft();
        }
    };

    const handleResetRightEye = (value) => {
        setRightEye(value);
        rightEyeRef.current = value;
        if (value === null) {
            formik.setFieldValue("image_file.right.image", null);
            // Clear model_outputs when user resets image - old ML data shouldn't persist
            formik.setFieldValue("image_file.right.model_outputs", { fundus: null, eyes: null, drdme: null });
            modelOutputsRef.current.right = { fundus: null, eyes: null, drdme: null };
            setRightGradable(undefined);
            setRightEyeProceeded(false);
            rightEyeProceededRef.current = false; // Reset ref
            teardownFilePondRight();
        }
    };

    const handleConfirmationCloseLeft = () => {
        setShowLeftImageConfirmation(false);
        setLeftEye(null);
        leftEyeRef.current = null;
        setLeftGradable(undefined);
        setLeftEyeProceeded(false);
        leftEyeProceededRef.current = false; // Reset ref
        formik.setFieldValue("image_file.left.image", null);
        // Clear model_outputs when user closes confirmation without confirming - old ML data shouldn't persist
        formik.setFieldValue("image_file.left.model_outputs", { fundus: null, eyes: null, drdme: null });
        modelOutputsRef.current.left = { fundus: null, eyes: null, drdme: null };
        teardownFilePondLeft();
    };

    const handleConfirmationCloseRight = () => {
        setShowRightImageConfirmation(false);
        setRightEye(null);
        rightEyeRef.current = null;
        setRightGradable(undefined);
        setRightEyeProceeded(false);
        rightEyeProceededRef.current = false; // Reset ref
        formik.setFieldValue("image_file.right.image", null);
        // Clear model_outputs when user closes confirmation without confirming - old ML data shouldn't persist
        formik.setFieldValue("image_file.right.model_outputs", { fundus: null, eyes: null, drdme: null });
        modelOutputsRef.current.right = { fundus: null, eyes: null, drdme: null };
        teardownFilePondRight();
    };

    // Function to run all model inferences on an image (background warmup / non-critical path)
    const runModelInferences = async (file) => {
        const warmupId = `warmup-${Date.now()}`;
        try {
            const fundusSess = fundusSession || await loadFundusModel();
            if (!fundusSess) return;

            await preprocessAndCacheTensor(onnxClient, warmupId, file);

            const { result: fundusResult } = await onnxClient.runModel('fundus', warmupId);
            if (fundusResult?.fundus_label === 'fundus') {
                await onnxClient.runModel('eyes',  warmupId).catch(() => {});
                await onnxClient.runModel('drdme', warmupId).catch(() => {});
            }
        } catch (_) {
            // warmup is best-effort
        } finally {
            onnxClient.clearTensor(warmupId).catch(() => {});
        }
    };

    const handleConfirmationYes = async (eye, fileOverride) => {
        setShowLeftImageConfirmation(false);
        setShowRightImageConfirmation(false);
        
        // Run validation (fileOverride when skipping confirmation dialog, else from state)
        let file = fileOverride;
        if (file == null && eye === 'left' && leftEye) {
            file = Array.isArray(leftEye) ? leftEye[0].file : leftEye;
        } else if (file == null && eye === 'right' && rightEye) {
            file = Array.isArray(rightEye) ? rightEye[0].file : rightEye;
        }
        
        if (file) {
            if (validationInFlightRef.current[eye]) {
                return;
            }
            validationInFlightRef.current[eye] = true;
            try {
                // Run complete validation flow (catch any unexpected throw so ErrorBoundary doesn't take over)
                const uploadResult = await handleImageUpload(file, eye);
                const canProceed = uploadResult && uploadResult.ok === true;
                const processedFile = canProceed ? uploadResult.processedFile : null;

            if (canProceed && processedFile) {
                if (!mobileSequentialOnnx) {
                    const items = makeValidatedPondFileItem(processedFile);
                    if (eye === 'left') {
                        leftEyeRef.current = items;
                        setLeftEye(items);
                    } else {
                        rightEyeRef.current = items;
                        setRightEye(items);
                    }
                }
                if (eye === 'left') {
                    const leftNorm = normalizeImageForDraft(processedFile);
                    if (leftNorm) {
                        formik.setFieldValue("image_file.left.image", leftNorm);
                    }
                    setLeftGradable("good");
                } else if (eye === 'right') {
                    const rightNorm = normalizeImageForDraft(processedFile);
                    if (rightNorm) {
                        formik.setFieldValue("image_file.right.image", rightNorm);
                    }
                    setRightGradable("good");
                }
                const leftDraftSrc = eye === 'left' ? processedFile : (leftEyeRef.current ?? leftEye);
                const rightDraftSrc = eye === 'right' ? processedFile : (rightEyeRef.current ?? rightEye);
                void persistScreeningImageDraftNow(leftDraftSrc, rightDraftSrc);
            } else {
                // Validation failed: only clear image when failure was due to models (after precheck), not when STEP 0 couldn't load sessions
                if (failedDueToModelsRef.current && !modelPrecheckFailedRef.current) {
                    if (eye === 'left') {
                        setLeftEye(null);
                        leftEyeRef.current = null;
                        setLeftGradable(undefined);
                        formik.setFieldValue("image_file.left.image", null);
                        formik.setFieldValue("image_file.left.model_outputs", { fundus: null, eyes: null, drdme: null });
                        modelOutputsRef.current.left = { fundus: null, eyes: null, drdme: null };
                        teardownFilePondLeft();
                    } else if (eye === 'right') {
                        setRightEye(null);
                        rightEyeRef.current = null;
                        setRightGradable(undefined);
                        formik.setFieldValue("image_file.right.image", null);
                        formik.setFieldValue("image_file.right.model_outputs", { fundus: null, eyes: null, drdme: null });
                        modelOutputsRef.current.right = { fundus: null, eyes: null, drdme: null };
                        teardownFilePondRight();
                    }
                    pendingImageRef.current = null;
                    setPendingEye(null);
                }
            }
            } catch (err) {
                // Prevent any unhandled rejection from reaching ErrorBoundary (grey "Something went wrong" page)
                setIsProcessing(false);
                const raw = err?.message || '';
                if (isModelUnavailableError(err)) {
                    setModelsErrorDialogTitle('Models not available');
                    setModelsUnavailableMessage(getModelsUnavailableUserMessage(raw) || MODELS_UNAVAILABLE_MSG);
                    failedDueToModelsRef.current = true;
                    modelPrecheckFailedRef.current = false;
                } else {
                    setModelsErrorDialogTitle(GENERIC_PROCESSING_ERROR_TITLE);
                    setModelsUnavailableMessage(raw || 'Image processing failed. Please try again.');
                    failedDueToModelsRef.current = false;
                    modelPrecheckFailedRef.current = false;
                }
                setShowModelsUnavailableError(true);
            } finally {
                validationInFlightRef.current[eye] = false;
            }
        }
    };

    // Function to trigger file selection for reupload
    const handleReuploadLeft = () => {
        teardownFilePondLeft();
        
        // Reset the current image state
        setLeftEye(null);
        leftEyeRef.current = null;
        setLeftGradable(undefined);
        leftEyeProceededRef.current = false; // Reset ref
        formik.setFieldValue("image_file.left.image", null);
        // Clear model_outputs when user reuploads - old ML data shouldn't persist
        formik.setFieldValue("image_file.left.model_outputs", { fundus: null, eyes: null, drdme: null });
        modelOutputsRef.current.left = { fundus: null, eyes: null, drdme: null };
        
        // Trigger file selection immediately
        if (leftFileInputRef.current) {
            leftFileInputRef.current.click();
        }
    };

    const handleReuploadRight = () => {
        teardownFilePondRight();
        
        // Reset the current image state
        setRightEye(null);
        rightEyeRef.current = null;
        setRightGradable(undefined);
        rightEyeProceededRef.current = false; // Reset ref
        formik.setFieldValue("image_file.right.image", null);
        // Clear model_outputs when user reuploads - old ML data shouldn't persist
        formik.setFieldValue("image_file.right.model_outputs", { fundus: null, eyes: null, drdme: null });
        modelOutputsRef.current.right = { fundus: null, eyes: null, drdme: null };
        
        // Trigger file selection immediately
        if (rightFileInputRef.current) {
            rightFileInputRef.current.click();
        }
    };

  return (
    <div style={{ paddingBottom: isMobile ? '100px' : '0' }}>
        {loading && <FullPageLoader/>}
        
        {/* Processing Screen */}
        {isProcessing && (
            <ImageProcessingScreen 
                message={processingMessage}
                subMessage={processingSubMessage}
            />
        )}
        
        {/* Duplicate Image Error Screen */}
        {showDuplicateError && (
            <DuplicateImageError 
                onReupload={handleDuplicateErrorReupload}
            />
        )}
        
        {/* Non-Fundus Error Screen */}
        {showNonFundusError && (
            <NonFundusError 
                onReupload={handleNonFundusErrorReupload}
                onProceed={handleNonFundusErrorProceed}
                attemptNumber={currentNonFundusAttempt}
                isSimilarityScoreIssue={isSimilarityScoreIssue}
            />
        )}
        
        {/* Wrong Eye Error Screen */}
        {showWrongEyeError && wrongEyeInfo && (
            <WrongEyeError 
                detectedEye={wrongEyeInfo.detectedEye}
                expectedEye={wrongEyeInfo.expectedEye}
                onReupload={handleWrongEyeErrorReupload}
                onProceed={handleWrongEyeErrorProceed}
            />
        )}
        
        {/* Models unavailable / processing error - do not show image as uploaded */}
        <Dialog open={showModelsUnavailableError} onClose={() => { setShowModelsUnavailableError(false); setModelsUnavailableMessage(''); setModelsErrorDialogTitle('Models not available'); }}>
            <DialogTitle>{modelsErrorDialogTitle}</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ whiteSpace: "pre-line" }}>
                    {modelsUnavailableMessage || 'AI models could not be loaded. Please connect to the internet, then log out and log back in to download the required models. You cannot proceed with screening until the models are available.'}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => { setShowModelsUnavailableError(false); setModelsUnavailableMessage(''); setModelsErrorDialogTitle('Models not available'); }} color="primary" autoFocus>OK</Button>
            </DialogActions>
        </Dialog>
        
        {/* Hidden file inputs for reupload functionality */}
        <input
            ref={leftFileInputRef}
            type="file"
            accept="image/*,.dcm,.dic,application/dicom"
            style={{ display: 'none' }}
            onChange={async (e) => {
                if (e.target.files && e.target.files[0]) {
                    const file = e.target.files[0];
                    if (isAcceptedEyeUploadFile(file)) {
                        // Show confirmation dialog FIRST, before validation
                            const fileItems = [{
                                file: file,
                                id: Date.now().toString(),
                                status: 1,
                                serverId: null
                            }];
                            leftEyeRef.current = fileItems;
                            setLeftEye(fileItems);
                            // CRITICAL: Restore pendingImage for proceed flow (after reupload)
                            pendingImageRef.current = file;
                            setPendingEye("left");
                            handleConfirmationYes("left", file);
                    }
                } else {
                    // User cancelled file picker - clear everything for left eye
                    pendingImageRef.current = null;
                    setPendingEye(null);
                    setLeftEye(null);
                    leftEyeRef.current = null;
                    setLeftGradable(undefined);
                    formik.setFieldValue("image_file.left.image", null);
                    // Clear model_outputs when user cancels reupload - old ML data shouldn't persist
                    formik.setFieldValue("image_file.left.model_outputs", { fundus: null, eyes: null, drdme: null });
                    modelOutputsRef.current.left = { fundus: null, eyes: null, drdme: null };
                    setIsProcessing(false);
                }
                // Reset the input
                e.target.value = '';
            }}
        />
        <input
            ref={rightFileInputRef}
            type="file"
            accept="image/*,.dcm,.dic,application/dicom"
            style={{ display: 'none' }}
            onChange={async (e) => {
                if (e.target.files && e.target.files[0]) {
                    const file = e.target.files[0];
                    if (isAcceptedEyeUploadFile(file)) {
                        // Show confirmation dialog FIRST, before validation
                            const fileItems = [{
                                file: file,
                                id: Date.now().toString(),
                                status: 1,
                                serverId: null
                            }];
                            rightEyeRef.current = fileItems;
                            setRightEye(fileItems);
                            // CRITICAL: Restore pendingImage for proceed flow (after reupload)
                            pendingImageRef.current = file;
                            setPendingEye("right");
                            handleConfirmationYes("right", file);
                    }
                } else {
                    // User cancelled file picker - clear everything for right eye
                    pendingImageRef.current = null;
                    setPendingEye(null);
                    setRightEye(null);
                    rightEyeRef.current = null;
                    setRightGradable(undefined);
                    formik.setFieldValue("image_file.right.image", null);
                    // Clear model_outputs when user cancels reupload - old ML data shouldn't persist
                    formik.setFieldValue("image_file.right.model_outputs", { fundus: null, eyes: null, drdme: null });
                    modelOutputsRef.current.right = { fundus: null, eyes: null, drdme: null };
                    setIsProcessing(false);
                }
                // Reset the input
                e.target.value = '';
            }}
        />
        <Container sx={{width:"100%", display: "flex",justifyContent:"space-evenly", paddingX: isMobile? "0px": "16px", height: isMobile? "auto": "", overflowY: "auto", boxSizing: "border-box", gap: isMobile?"5px":"10px"}}>
            <Box sx={{width: isMobile? "45%": "24vw",}}>
            <Box sx={{border: "1px solid rgba(0, 0, 0, 0.05)", borderRadius: "8px", display: isMobile?"flex":"", flexDirection:isMobile?"column":"", gap: isMobile? "5px":""}}>
                <Container sx={{background: "#EAF4FA", padding: "1% 0"}}>
                    <Typography display="flex" alignItems="center" color="#0D1E83" fontSize="2.3vh" fontWeight="700" justifyContent="center">
                        R
                        <VisibilityOutlinedIcon sx={{height: "3em", width: "3em"}}/>
                    </Typography>
                    <Typography display="flex" justifyContent="center" color="#949494" fontWeight="400">
                        Right Eye
                    </Typography>
                </Container>
                <Container sx={{ minHeight: isMobile?"8vh":"20vh", padding:  isMobile?"0px":"12px"}}>
                    <Box sx={{border: "1px dashed #B8B3B4", borderRadius: "4px", padding: isMobile?"8px 0px":"5px", height: isMobile?"auto":"", background:isMobile && !rightEye?"#F0EEEE":""}}>
                        {isValidImageData(rightEye) ?
                            <ImageUploaded
                                key={rightEyeProceeded ? `right-proceeded-${rightEye?.[0]?.id || Date.now()}` : `right-${rightEye?.[0]?.id || 'normal'}`}
                                files={rightEye}
                                resetFile={handleResetRightEye}
                                resetGradable={setRightGradable}
                                eye={"patient's right eye"}
                                setDisabledProceed={setDisabledProceed}
                                onReupload={handleReuploadRight}
                            />
                            :
                            <FilePond
                                key={`fp-right-${rightFilePondMountKey}`}
                                ref={rightFilePondRef}
                                file={rightEye}
                                required={!singleImageMode}
                                credits={false}
                                acceptedFileTypes={['image/*', 'application/dicom']}
                                fileValidateTypeDetectType={(source, type) =>
                                    new Promise((resolve) => {
                                        resolve(isDicomExtension(source) ? 'application/dicom' : type);
                                    })
                                }
                                checkValidity={true}
                                labelIdle= {isMobile? '<span class="filepond--label-action">Select Eye Image</span><br/><span class="filpond--support">Supports: jpg, jpeg, png, dcm</span>' :'Drop image here, or <span class="filepond--label-action">Browse</span><br/><span class="filpond--support">Supports: jpg, jpeg, png, dcm (DICOM)</span>'}
                                onupdatefiles={async (fileItems) => {
                                    if (fileItems.length === 0) {
                                        lastValidatedPondItemIdRef.current.right = null;
                                        setRightEye(null);
                                        rightEyeRef.current = null;
                                        pendingImageRef.current = null;
                                        setPendingEye(null);
                                      } else if (fileItems[0].file && isAcceptedEyeUploadFile(fileItems[0].file)) {
                                        const item = fileItems[0];
                                        const ready =
                                            item.status === FileStatus.IDLE ||
                                            item.status === FileStatus.PROCESSING_COMPLETE;
                                        if (!ready) return;
                                        if (lastValidatedPondItemIdRef.current.right === item.id) return;
                                        lastValidatedPondItemIdRef.current.right = item.id;
                                        rightEyeRef.current = fileItems;
                                        setRightEye(fileItems);
                                        pendingImageRef.current = item.file;
                                        setPendingEye("right");
                                        handleConfirmationYes("right", item.file);
                                      } else if (fileItems[0].error) {
                                        // FilePond error
                                      }
                                }}
                            />
                        }
                    </Box>
                </Container>
                </Box>
                <Container sx={{Width:"100%", marginTop:"10px", paddingBottom: "12px"}}>
                    { rightGradable === "good" && <ImageGood />}
                    { rightGradable === "proceeded" && <ImageUploadedStatus />}
                    { rightGradable === "bad" && <ImageBad eye={"Right Eye"}/>}
                </Container>
            </Box>
            <Box sx={{width: isMobile? "45%": "24vw",}}>
            <Box sx={{border: "1px solid rgba(0, 0, 0, 0.05)", borderRadius: "8px", display: isMobile?"flex":"", flexDirection:isMobile?"column":"", gap: isMobile? "5px":""}}>
                <Container sx={{background: "#EAF4FA", padding: "1% 0"}}>
                    <Typography display="flex" alignItems="center" color="#0D1E83" fontSize="2.3vh" fontWeight="700" justifyContent="center">
                        <VisibilityOutlinedIcon sx={{height: "3em", width: "3em"}}/>
                        L
                    </Typography>
                    <Typography display="flex" justifyContent="center" color="#949494" fontWeight="400">
                        Left Eye
                    </Typography>
                </Container>
                <Container sx={{ minHeight: isMobile?"8vh":"20vh", padding:  isMobile?"0px":"12px"}}>
                    <Box sx={{border: "1px dashed #B8B3B4", borderRadius: "4px", padding: isMobile?"8px 0px":"5px", height: isMobile?"auto":"", background:isMobile && !leftEye?"#F0EEEE":""}}>
                        {isValidImageData(leftEye) ?
                            <ImageUploaded
                                key={leftEyeProceeded ? `left-proceeded-${leftEye?.[0]?.id || Date.now()}` : `left-${leftEye?.[0]?.id || 'normal'}`}
                                files={leftEye}
                                resetFile={handleResetLeftEye}
                                resetGradable={setLeftGradable}
                                eye={"patient's left eye"}
                                setDisabledProceed={setDisabledProceed}
                                onReupload={handleReuploadLeft}
                            />
                            :
                            <FilePond
                                key={`fp-left-${leftFilePondMountKey}`}
                                ref={leftFilePondRef}
                                file={leftEye}
                                required={!singleImageMode}
                                credits={false}
                                acceptedFileTypes={['image/*', 'application/dicom']}
                                fileValidateTypeDetectType={(source, type) =>
                                    new Promise((resolve) => {
                                        resolve(isDicomExtension(source) ? 'application/dicom' : type);
                                    })
                                }
                                checkValidity={true}
                                labelIdle= {isMobile? '<span class="filepond--label-action">Select Eye Image</span><br/><span class="filpond--support">Supports: jpg, jpeg, png, dcm</span>' :'Drop image here, or <span class="filepond--label-action">Browse</span><br/><span class="filpond--support">Supports: jpg, jpeg, png, dcm (DICOM)</span>'}
                                onupdatefiles={async (fileItems) => {
                                    if (fileItems.length === 0) {
                                        lastValidatedPondItemIdRef.current.left = null;
                                        setLeftEye(null);
                                        leftEyeRef.current = null;
                                        pendingImageRef.current = null;
                                        setPendingEye(null);
                                      } else if (fileItems[0].file && isAcceptedEyeUploadFile(fileItems[0].file)) {
                                        const item = fileItems[0];
                                        const ready =
                                            item.status === FileStatus.IDLE ||
                                            item.status === FileStatus.PROCESSING_COMPLETE;
                                        if (!ready) return;
                                        if (lastValidatedPondItemIdRef.current.left === item.id) return;
                                        lastValidatedPondItemIdRef.current.left = item.id;
                                        leftEyeRef.current = fileItems;
                                        setLeftEye(fileItems);
                                        pendingImageRef.current = item.file;
                                        setPendingEye("left");
                                        handleConfirmationYes("left", item.file);
                                      } else if (fileItems[0].error) {
                                        // FilePond error
                                      }
                                }}
                            />
                        }
                    </Box>
                </Container>
                </Box>
                <Container sx={{ marginTop: isMobile?"5px":"10px", paddingBottom: isMobile?"5px":"12px"}}>
                    {leftGradable === "good" && <ImageGood />}
                    {leftGradable === "proceeded" && <ImageUploadedStatus />}
                    {leftGradable === "bad" && <ImageBad eye={"Left Eye"}/>}
                </Container>
            </Box>
        </Container>
        
        {/* Checkbox positioned right below the eye upload sections for mobile */}
        {isMobile && (
            <Box sx={{ 
                padding: '6px 16px', 
                display: 'flex', 
                justifyContent: 'flex-start',
                width: '100%',
                backgroundColor: '#fff',
                borderTop: '1px solid #f0f0f0'
            }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={singleImageMode}
                            onChange={(e) => {
                                setSingleImageMode(e.target.checked);
                                if(!e.target.checked) {
                                    setSelectedSingleImageReason('');
                                    formik.setFieldValue("image_file.single_image_reason", '');
                                }
                            }}
                            sx={{
                                color: '#0D1E83',
                                '&.Mui-checked': {
                                    color: '#0D1E83',
                                },
                            }}
                        />
                    }
                    label="Only one eye image available"
                    sx={{
                        color: '#0D1E83',
                        fontSize: '14px',
                        fontWeight: 500,
                    }}
                />
            </Box>
        )}
        
          {/* Warning message for single image mode conflict */}
          {showSingleImageWarning && (
              <Box
                  sx={{
                      position: isMobile ? 'fixed' : 'static',
                      bottom: isMobile ? '80px' : 'auto',
                      left: isMobile ? '16px' : 'auto',
                      right: isMobile ? '16px' : 'auto',
                      zIndex: isMobile ? 1301 : 'auto',
                      backgroundColor: '#FFF3CD',
                      border: '1px solid #FFEAA7',
                      borderRadius: '8px',
                      padding: '12px 16px',
                      marginBottom: isMobile ? '16px' : '8px',
                      textAlign: 'center'
                  }}
              >
                  <Typography
                      sx={{
                          color: '#856404',
                          fontSize: '14px',
                          fontWeight: 500
                      }}
                  >
                      ℹ️ Both eye images detected. Single image mode has been disabled.
                  </Typography>
              </Box>
          )}
          
          {/* Checkbox for desktop */}
          {!isMobile && (
              <Box sx={{ 
                  padding: '12px 0', 
                  display: 'flex', 
                  justifyContent: 'flex-start',
                  width: '100%'
              }}>
                  <FormControlLabel
                      control={
                          <Checkbox
                              checked={singleImageMode}
                              onChange={(e) => {
                                  setSingleImageMode(e.target.checked);
                                  if(!e.target.checked) {
                                      setSelectedSingleImageReason('');
                                      formik.setFieldValue("image_file.single_image_reason", '');
                                  }
                              }}
                              sx={{
                                  color: '#0D1E83',
                                  '&.Mui-checked': {
                                      color: '#0D1E83',
                                  },
                              }}
                          />
                      }
                      label="Only one eye image available"
                      sx={{
                          color: '#0D1E83',
                          fontSize: '14px',
                          fontWeight: 500,
                      }}
                  />
              </Box>
          )}
          
          {/* Inline Radio Options for Single Image Mode */}
          {singleImageMode && (
              <Box sx={{
                  padding: '16px',
                  backgroundColor: '#EAF4FA',
                  borderTop: '1px solid #f0f0f0',
                  marginTop: isMobile ? 0 : '8px',
                  borderRadius: '8px'
              }}>
                  <RadioGroup
                      value={selectedSingleImageReason}
                      onChange={handleSingleImageReasonChange}
                  >
                      {options.map((option) => (
                          <FormControlLabel
                              key={option.value}
                              value={option.value}
                              control={
                                  <Radio
                                      sx={{
                                          color: '#0D1E83',
                                          '&.Mui-checked': { color: '#0D1E83' }
                                      }}
                                  />
                              }
                              label={option.label}
                              sx={{
                                  mb: 1.5,
                                  '& .MuiFormControlLabel-label': {
                                      color: selectedSingleImageReason === option.value ? '#0D1E83' : '#666666',
                                      fontWeight: selectedSingleImageReason === option.value ? 500 : 400,
                                      fontSize: '16px'
                                  }
                              }}
                          />
                      ))}
                  </RadioGroup>
              </Box>
          )}

          {/* Buttons section */}
          <div
              style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  position: isMobile ? 'fixed' : 'static',
                  bottom: isMobile ? 0 : 'auto',
                  left: 0,
                  width: isMobile ? '100%' : 'auto',
                  padding: isMobile ? '12px 16px' : '0',
                  background: isMobile ? '#fff' : 'transparent',
                  boxShadow: isMobile ? '0 -2px 8px rgba(0,0,0,0.1)' : 'none',
                  zIndex: isMobile ? 1300 : 'auto',
                  marginTop: isMobile ? 0 : '16px',
                  gap: isMobile ? '12px' : '16px',
                  boxSizing: 'border-box'
              }}
          >
              <Button 
                  onClick={handleBack} 
                  startIcon={<ArrowBackIcon />}
                  sx={{ 
                      color: "#0D1E83", 
                      textTransform: "none",
                      border: "1px solid #0D1E83",
                      borderRadius: "8px",
                      px: 2,
                      py: 1.5,
                      fontSize: "16px",
                      fontWeight: 400
                  }}
              >
                  Back
              </Button>
              <Button
                  variant="contained"
                  disabled={disabledProceed}
                  sx={{
                      borderRadius: "8px",
                      fontWeight: 400,
                      fontSize: "16px",
                      py: 1.5,
                      px: 3,
                      background: disabledProceed
                          ? "var(--grey, #F4EFF0)"
                          : 'linear-gradient(270deg, #051572 7.32%, #0820B4 92.68%)',
                      border: disabledProceed
                          ? '1px solid rgba(125, 121, 123, 0.50)'
                          : '1px solid #7D797B80',
                    textTransform: "none"
                  }}
                  endIcon={<ArrowForwardIcon />}
                  onClick={handleProceed}
              >
                  Proceed
              </Button>
          </div>



    </div>
  )
}

export default ImageUpload